import React, { useEffect, useMemo, useState } from 'react';

import { last } from 'lodash';
import { useFieldArray, useForm } from 'react-hook-form';

import { useCustomSnackBar } from 'hooks/useCustomSnackBar';

import { useProjectTreeStore } from 'store/projectContract.store';

import { useQueryGetProgramGeneralHierarchyList } from 'api/queries/getProgramGeneralHierarchyList.query';

import { TooltipPosition } from 'components/common/CustomizedTooltip/customizedTooltip.enums';
import { TreeCrumbs } from 'components/common/TreeCrumbs/TreeCrumbs';
import PlaniswareTable from 'components/common/UploadFileModal/components/UploadPlaniswereModal/components/PlaniswareTable/planiswareTable';
import { Details } from 'components/common/UploadFileModal/components/programLibrary.types';
import classes from 'components/common/UploadFileModal/programLibraryUploadFiles.module.scss';
import { CustomizedTooltip } from 'components/common/index';

import { Button } from 'components/DesignSystem/Button';
import { Modal } from 'components/DesignSystem/Modal';

const selectLastSubProgram = ({ projectHierarchyList, storedProject }) => {
    const p = projectHierarchyList?.children.find((c) => c.id === storedProject?.id);
    if (p) {
        const contract: any = last(p.children);
        if (contract) {
            return {
                id: contract.id,
                title: contract.name,
                uuid: contract.uuid,
            };
        }
    }
};

interface Props {
    details: Details;
    isUploading: boolean;
    handleFetchData: any;
    onClose: () => void;
    open: boolean;
}

export const UploadPlaniswereModal = ({
    details: detailsFromFile,
    isUploading,
    handleFetchData,
    onClose,
    open,
}: Props) => {
    const { project: storedProject, contract: storedContract } = useProjectTreeStore();
    const [selectedProject, setSelectedProject] = useState<any>(null);
    const [selectedContract, setSelectedContract] = useState<any>(null);
    const { data: projectHierarchyList, isLoading: loadingGeneralList } =
        useQueryGetProgramGeneralHierarchyList('ProgramLibraryUploadFiles');
    const { handleSnackBar } = useCustomSnackBar();

    const onSelectionFinish = ({ project, contract }) => {
        setSelectedProject(project);
        setSelectedContract(contract);
    };

    const {
        control,
        register,
        handleSubmit,
        watch,
        setValue,
        formState: { errors },
    } = useForm<Details>({
        values: detailsFromFile,
    });

    const { fields } = useFieldArray({
        control,
        name: 'versions',
    });
    const details = watch();

    const selectedNode = useMemo(() => {
        return storedContract || selectLastSubProgram({ projectHierarchyList, storedProject });
    }, [storedProject, storedContract, projectHierarchyList]);

    useEffect(() => {
        selectedNode && setSelectedContract(selectedNode);
    }, [selectedNode]);

    useEffect(() => {
        storedProject && setSelectedProject(storedProject);
        storedContract && setSelectedContract(storedContract);
    }, [storedProject, storedContract]);

    const handleFinishUpload = () => {
        if (!selectedContract) {
            handleSnackBar('Sub-Program is mandatory', 'error');
        } else {
            const payload = { details, contract: selectedContract, project: selectedProject };
            handleFetchData(payload);
        }
    };

    const disabledPlanisware = details && details.versions[0].versionName?.length < 2;

    return (
        <Modal
            open={open}
            onClose={onClose}
            showCloseButton={!isUploading}
            actions={
                <>
                    <Button size={'medium'} variant={'secondary'} disabled={isUploading} onClick={onClose}>
                        Cancel
                    </Button>

                    {!details.relationsFile && (
                        <Button size={'medium'} variant={'primary'} component={'label'} disabled={disabledPlanisware}>
                            Upload Relationships
                            <input
                                {...register('relationsFile', { value: null })}
                                onChange={(e) =>
                                    setValue(
                                        'relationsFile',
                                        e.target.files && e.target.files.length > 0 ? e.target.files[0] : null,
                                    )
                                }
                                hidden
                                type={'file'}
                                id={'file'}
                                accept={'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}
                            />
                        </Button>
                    )}

                    <Button
                        type={'submit'}
                        size={'medium'}
                        variant={'primary'}
                        disabled={isUploading}
                        isLoading={isUploading}
                        onClick={handleSubmit(handleFinishUpload)}
                    >
                        Upload Activities
                    </Button>
                </>
            }
            title={'Upload Planisware File'}
        >
            <form>
                <div className={classes.ProgramLibraryUploadFilesContainer}>
                    <div className={classes.uploadFilesHeader}>
                        <div className={classes.uploadFilesHeaderSection}>
                            {loadingGeneralList ? (
                                <div>Loading</div>
                            ) : (
                                selectedContract?.uuid && (
                                    <TreeCrumbs
                                        selectedNode={selectedContract.uuid}
                                        onSelectionFinish={onSelectionFinish}
                                        data={projectHierarchyList}
                                        showAddNewButton={false}
                                        showAll={false}
                                    />
                                )
                            )}
                        </div>
                        <div className={classes.uploadFilesHeaderSection}>
                            <span>Source File:</span>
                            <span className={classes.uploadFilesHeaderName}>
                                <CustomizedTooltip
                                    tooltipPosition={TooltipPosition.Center}
                                    triggerElement={<div className={classes.ellipsis}>{details.file?.name}</div>}
                                    tooltipContent={details.file?.name}
                                />
                            </span>
                        </div>
                    </div>
                    <div className={classes.uploadFilesHeaderSentences}>
                        {!details.relationsFile ? (
                            <div>Import Schedule from Excel</div>
                        ) : (
                            <div>Relations file: {details.relationsFile.name}</div>
                        )}
                    </div>

                    <PlaniswareTable
                        fields={fields}
                        control={control}
                        setValue={setValue}
                        errors={errors}
                        contract={selectedContract}
                    />
                </div>
            </form>
        </Modal>
    );
};
